import React, { useEffect, useState, useRef } from 'react';
import { useForm } from "react-hook-form";
import { CSVLink } from 'react-csv';
import { Grid, Alert, CircularProgress, Button } from '@mui/material';
import DateRangePicker from '../DateRangePicker';
import MUIModal from '../../common/MUIModal';
import { FormProvider, RHFSelectField } from '../../common/hook-form';
import { formatDate, supportTicketStatus } from '../../helpers/UtilityHelper';
import { apiConstants, getGeneric, post } from '../../common/constants';
import { useSnackbar } from 'notistack';
import { FONT, WEIGHT } from '../../styles/foundation/typography';
import { PRIMARY_BLUE } from '../../styles/foundation/colors';

const ExportTicketData = ({ show, handleClose, merchantOid, statusList }) => {

    const initialDateRange = [{
        endDate: new Date(),
        startDate: new Date(),
        key: 'selection'
    }];
    const [dateRange, setDateRange] = useState(initialDateRange);
    const [csvData, setCsvData] = useState([]);
    const [dataReady, setDataReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const csvLinkRef = useRef(null);

    const methods = useForm({
        defaultValues: {
            status: 'all',
        }
    });
    const { handleSubmit } = methods;

    useEffect(() => {
        if (!show) {
            setDataReady(false); // Reset data readiness when the modal is closed
            setIsLoading(false);
            setCsvData([]); // Clear CSV data when modal closes
        }
    }, [show]);

    const onSubmit = async (filters) => {
        setErrorMessage('');
        const startDate = dateRange[0].startDate;
        const endDate = dateRange[0].endDate;
        const dateDifference = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

        if (dateDifference > 31) {
            setErrorMessage('The selected date range should not exceed 31 days.');
            return; // Exit the function early
        }

        setIsLoading(true);
        setDataReady(false);

        const allData = [];
        const currentDay = new Date(dateRange[0].startDate);

        // Utility function to create a delay
        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

        // Loop through each day in the date range
        while (currentDay <= dateRange[0].endDate) {
            const payloadBase = {
                start_date: formatDate(currentDay, "yyyy-MM-dd"),
                end_date: formatDate(currentDay, "yyyy-MM-dd"),
                merchantOid: merchantOid,
                page: 1, // Start with page 1
            };

            if (filters.status && filters.status !== 'all') {
                payloadBase.status_list = [filters.status];
            }

            try {
                // Initial request to get data for the specific day
                let response = await post(`${apiConstants.BASE_LOCAL}${apiConstants.FILTER_FRESHDESK}`, payloadBase);
                console.log(response,'response');
                
                // Check if responseObj exists and is not empty
                if (!response.data?.responseObj || response.data.responseObj.trim() === '') {
                    console.warn(`No data received for date ${formatDate(currentDay, "yyyy-MM-dd")}`);
                    // Move to next day without adding data
                    currentDay.setDate(currentDay.getDate() + 1);
                    continue;
                }

                const parsedRes = JSON.parse(response.data.responseObj);
                
                // Check if parsed response has the expected structure
                if (!parsedRes || typeof parsedRes !== 'object') {
                    console.warn(`Invalid response structure for date ${formatDate(currentDay, "yyyy-MM-dd")}`);
                    currentDay.setDate(currentDay.getDate() + 1);
                    continue;
                }

                const totalCount = parsedRes.total || 0; // This should be provided by the API
                const totalPages = Math.min(Math.ceil(totalCount / 30), 10); // Handle up to 10 pages max
                let dayData = parsedRes.results || []; // Assuming the data is in 'results' key

                // Fetch subsequent pages for the day
                for (let page = 2; page <= totalPages; page++) {
                    payloadBase.page = page;
                    response = await post(`${apiConstants.BASE_LOCAL}${apiConstants.FILTER_FRESHDESK}`, payloadBase);
                    
                    // Check if responseObj exists and is not empty for subsequent pages
                    if (!response.data?.responseObj || response.data.responseObj.trim() === '') {
                        console.warn(`No data received for page ${page} on date ${formatDate(currentDay, "yyyy-MM-dd")}`);
                        break; // Break out of page loop
                    }
                    
                    const parsedRes1 = JSON.parse(response.data?.responseObj);
                    if (parsedRes1 && parsedRes1?.results) {
                        dayData = dayData.concat(parsedRes1.results); // Concatenate records from each page
                    }
                }

                // Accumulate data from all days
                if (dayData.length > 0) {
                    allData.push(...dayData);
                }
            } catch (error) {
                console.error(`Error fetching data for date ${formatDate(currentDay, "yyyy-MM-dd")}:`, error);
                // Instead of returning, continue to next day
                console.warn(`Skipping date ${formatDate(currentDay, "yyyy-MM-dd")} due to error`);
            }

            // Delay for 1 second before moving to the next day
            await delay(500);

            // Move to the next day
            currentDay.setDate(currentDay.getDate() + 1);
        }

        // Helper function to clean CSV data
        const cleanCsvField = (field) => {
            if (!field) return '';
            // Convert to string and remove problematic characters
            let cleaned = String(field);
            // Remove or replace characters that break CSV
            cleaned = cleaned.replace(/[\r\n\t]/g, ' '); // Replace line breaks and tabs with spaces
            cleaned = cleaned.replace(/,/g, ';'); // Replace commas with semicolons
            cleaned = cleaned.replace(/"/g, "'"); // Replace double quotes with single quotes
            return cleaned.trim();
        };

        // Check if we have any data to export
        if (allData.length === 0) {
            setErrorMessage('No data found for the selected date range and filters.');
            setIsLoading(false);
            return;
        }

        // Convert all accumulated data to CSV format
        const csvExportData = allData.map(item => ({
            id: cleanCsvField(item?.id),
            awb: cleanCsvField(item?.custom_fields?.cf_awb_number387695),
            category: cleanCsvField(item?.custom_fields?.cf_category),
            subCategory: cleanCsvField(item?.custom_fields?.cf_sub_category),
            user: cleanCsvField(item?.custom_fields?.cf_user_name),
            status: cleanCsvField(statusList.find((val) => val.id === item?.status)?.label_for_customers),
            dateCreated: formatDate(item?.created_at, "yyyy-MM-dd"),
            lastUpdate: formatDate(item?.updated_at, "yyyy-MM-dd"),
            resolvedBy: cleanCsvField(item?.custom_fields?.cf_closed_by_name || item?.custom_fields?.cf_resolved_by_name),
            description: cleanCsvField(item?.description_text),
        }));

        setCsvData(csvExportData);
        setDataReady(true);
        setIsLoading(false);
        
        // Auto-trigger CSV download after a short delay
        setTimeout(() => {
            if (csvLinkRef.current) {
                csvLinkRef.current.link.click();
                // Close modal after download
                setTimeout(() => {
                    handleClose();
                }, 500);
            }
        }, 100);
    };



    return (
        <MUIModal open={show} maxWidth="lg" title="Export Tickets Data" handleClose={handleClose}>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                    <Grid item xs={2.6}>
                        <DateRangePicker
                            defaultRange={dateRange}
                            maxDate={new Date()}
                            onChange={(range) => setDateRange(range)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <RHFSelectField
                            label="Status"
                            name="status"
                            options={[
                                { value: 'all', label: 'All' },
                                ...statusList?.map((val) => ({
                                    value: val.id,
                                    label: val.label_for_customers
                                }))
                            ]}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <Button
                            disabled={isLoading || dataReady}
                            type="submit"
                            variant="contained"
                            startIcon={isLoading ? <CircularProgress size={20} color="warning" /> : null}
                            sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.semibold, borderRadius: '8px', bgcolor: PRIMARY_BLUE, '&:hover': { bgcolor: PRIMARY_BLUE } }}
                        >
                            { isLoading ? 'Download In Progress...' : 'Fetch & Download Data'}
                        </Button>
                        
                        {/* Hidden CSV Link for auto-download */}
                        <CSVLink
                            ref={csvLinkRef}
                            data={csvData}
                            headers={[
                                { label: "Ticket ID", key: "id" },
                                { label: "AWB", key: "awb" },
                                { label: "Category", key: "category" },
                                { label: "Sub Category", key: "subCategory" },
                                { label: "Created By", key: "user" },
                                { label: "Current Status", key: "status" },
                                { label: "Created Date", key: "dateCreated" },
                                { label: "Last Updated Date", key: "lastUpdate" },
                                { label: "Resolved By", key: "resolvedBy" },
                                { label: "Description", key: "description" }
                            ]}
                            filename={"ems_export.csv"}
                            style={{ display: 'none' }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {errorMessage && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {errorMessage}
                            </Alert>
                        )}
                    </Grid>
                </Grid>
            </FormProvider>
        </MUIModal>
    );
};

export default ExportTicketData;
